import {
	BulkStatusUpdateDirection,
	BulkStatusUpdateTarget,
} from "../../../api/bulk-order-operations";

/**
 * The statuses this category can set, mirroring BulkStatusUpdateMap.cs on the API.
 *
 * The pairs shown here are display only — the API resolves them again from its own map and
 * never trusts anything the client believed. They are listed so the operator can see exactly
 * what is about to be written, which matters because (14,6) and (6,14) both read "Dorëzuar"
 * but say opposite things about who is holding the parcel.
 */
export interface StatusUpdateOption {
	target: BulkStatusUpdateTarget;
	labelKey: string;
	fallbackLabel: string;
	/** Always the sub-status — 14 is never written to StatusHistory. */
	historyStatusId: number;
	/** Written to dbo.Orders, per direction. Null means not offered in that direction. */
	pairs: Record<BulkStatusUpdateDirection, string | null>;
	defaultComment: string;
}

export const STATUS_UPDATE_OPTIONS: StatusUpdateOption[] = [
	{
		target: "InTransport",
		labelKey: "statusInTransport",
		fallbackLabel: "Në transport",
		historyStatusId: 5,
		pairs: { Local: "5", Outbound: "(14, 5)", Inbound: "(5, 14)" },
		defaultComment: "Porosia eshte vendosur ne transport",
	},
	{
		target: "Waiting",
		labelKey: "statusWaiting",
		fallbackLabel: "Në pritje",
		historyStatusId: 8,
		pairs: { Local: "8", Outbound: "(14, 8)", Inbound: "(8, 14)" },
		defaultComment: "Porosia eshte vendosur ne pritje",
	},
	{
		target: "Refused",
		labelKey: "statusRefused",
		fallbackLabel: "Refuzuar",
		historyStatusId: 7,
		pairs: { Local: "7", Outbound: "(14, 7)", Inbound: "(7, 14)" },
		defaultComment: "Porosia eshte refuzuar",
	},
	{
		target: "Delivered",
		labelKey: "statusDelivered",
		fallbackLabel: "Dorëzuar",
		historyStatusId: 6,
		pairs: { Local: "6", Outbound: "(14, 6)", Inbound: "(6, 14)" },
		defaultComment: "Porosia eshte dorezuar",
	},
	{
		// "Duke u kthyer" exists only as (14, 10) in OrderRepository's label table, so it is
		// offered only for an outbound batch — there is no local or inbound equivalent.
		target: "Returning",
		labelKey: "statusReturning",
		fallbackLabel: "Duke u kthyer",
		historyStatusId: 10,
		pairs: { Local: null, Outbound: "(14, 10)", Inbound: null },
		defaultComment: "Porosia eshte duke u kthyer",
	},
	{
		// (10, 14) in BOTH international directions — it is the only pair meaning "Kthyer"
		// alongside a 14. Writing (14, 10) instead would relabel it "Duke u kthyer".
		target: "Returned",
		labelKey: "statusReturned",
		fallbackLabel: "Kthyer",
		historyStatusId: 10,
		pairs: { Local: "10", Outbound: "(10, 14)", Inbound: "(10, 14)" },
		defaultComment: "Porosia eshte kthyer",
	},
	{
		// "Fletë Kthim" is (18, null) in OrderRepository's label table — a local pair only.
		// The 18s that carry an international half read differently ((18,10) and (18,13) are
		// "Flete Kthim internacional", (18,19) is "Kthim Flete Kthim internacional"), so this
		// status is offered for a local batch and nothing else.
		target: "ReturnSheet",
		labelKey: "statusReturnSheet",
		fallbackLabel: "Fletë Kthim",
		historyStatusId: 18,
		pairs: { Local: "18", Outbound: null, Inbound: null },
		defaultComment: "Porosia eshte vendosur ne flete kthim",
	},
	{
		// "Kthim Fletë Kthim" is (19, null). (19,10) and (19,19) exist too and read the same,
		// but neither is a (X, 14)/(14, X) pair, so there is no international orientation to
		// offer here either.
		target: "ReturnSheetReturn",
		labelKey: "statusReturnSheetReturn",
		fallbackLabel: "Kthim Fletë Kthim",
		historyStatusId: 19,
		pairs: { Local: "19", Outbound: null, Inbound: null },
		defaultComment: "Porosia eshte kthyer nga flete kthimi",
	},
];

export const getStatusesForDirection = (direction: BulkStatusUpdateDirection) =>
	STATUS_UPDATE_OPTIONS.filter((option) => option.pairs[direction] !== null);
